/**
 * Tenants API - control-plane tenant management endpoints
 */

import type {
  InternalTenantListResponse,
  CreateInternalTenantRequest,
  CreateInternalTenantResponse,
  InternalTenantOverviewResponse,
  InternalTenantUsersResponse,
  InternalTenantProjectsResponse,
  UpdateInternalTenantRequest,
  UpdateInternalTenantResponse,
  InternalTenantAgentModelsResponse,
  UpdateInternalTenantAgentModelsRequest,
  TenantStatus,
} from '@resolve-ai/shared';

import { fetchWithAuth } from './api-client';

export interface ListTenantsParams {
  status?: TenantStatus;
  search?: string;
}

export const tenantsApi = {
  async list(params: ListTenantsParams = {}): Promise<InternalTenantListResponse> {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchWithAuth<InternalTenantListResponse>(`/tenants${qs ? `?${qs}` : ''}`);
  },

  async create(payload: CreateInternalTenantRequest): Promise<CreateInternalTenantResponse> {
    return fetchWithAuth<CreateInternalTenantResponse>('/tenants', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async getOverview(tenantId: string): Promise<InternalTenantOverviewResponse> {
    return fetchWithAuth<InternalTenantOverviewResponse>(`/tenants/${tenantId}`);
  },

  async getUsers(tenantId: string): Promise<InternalTenantUsersResponse> {
    return fetchWithAuth<InternalTenantUsersResponse>(`/tenants/${tenantId}/users`);
  },

  async getProjects(tenantId: string): Promise<InternalTenantProjectsResponse> {
    return fetchWithAuth<InternalTenantProjectsResponse>(`/tenants/${tenantId}/projects`);
  },

  async update(tenantId: string, payload: UpdateInternalTenantRequest): Promise<UpdateInternalTenantResponse> {
    return fetchWithAuth<UpdateInternalTenantResponse>(`/tenants/${tenantId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  async getAgentModels(tenantId: string): Promise<InternalTenantAgentModelsResponse> {
    return fetchWithAuth<InternalTenantAgentModelsResponse>(`/tenants/${tenantId}/agent-models`);
  },

  async updateAgentModels(
    tenantId: string,
    payload: UpdateInternalTenantAgentModelsRequest
  ): Promise<InternalTenantAgentModelsResponse> {
    return fetchWithAuth<InternalTenantAgentModelsResponse>(`/tenants/${tenantId}/agent-models`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};
